import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Play, Square, RotateCw, RefreshCcw, Copy, Eye, EyeOff, Server, AlertTriangle, Loader2 } from "lucide-react";
import { API, authHeaders } from "@/lib/site";

const STATUS_STYLE = {
  running: "bg-emerald-500",
  offline: "bg-slate-400",
  provisioning: "bg-amber-500 animate-pulse",
};

export const Confirm = ({ title, body, confirmLabel, onCancel, onConfirm, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm px-4">
    <div className="w-full max-w-sm rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-7 shadow-2xl" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="p-2.5 rounded-full bg-amber-500/10 text-amber-500"><AlertTriangle className="w-5 h-5" /></span>
        <h3 className="font-display font-bold text-lg">{title}</h3>
      </div>
      <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">{body}</p>
      <div className="mt-7 flex gap-3">
        <button onClick={onCancel} data-testid={`${testid}-cancel`}
                className="flex-1 px-4 py-3 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm} data-testid={`${testid}-confirm`}
                className="flex-1 px-4 py-3 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors">
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

export const StatusDot = ({ status }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
    <span className={`w-2.5 h-2.5 rounded-full ${STATUS_STYLE[status] || "bg-slate-400"} ${status === "running" ? "shadow-[0_0_10px_2px_rgba(16,185,129,0.5)]" : ""}`} />
    {status}
  </span>
);

export default function ServersTab() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [showPw, setShowPw] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/servers`, { headers: authHeaders() });
      setServers(data.servers);
    } catch {
      toast.error("Could not load servers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const power = async (serverId, action) => {
    setBusy(`${serverId}-${action}`);
    try {
      const { data } = await axios.post(`${API}/servers/${serverId}/power`, { action }, { headers: authHeaders() });
      setServers((ss) => ss.map((s) => (s.server_id === serverId ? { ...s, status: data.status } : s)));
      toast.success(`Server ${action === "stop" ? "stopped" : action === "start" ? "started" : "rebooted"}.`);
    } catch {
      toast.error("Power action failed.");
    } finally {
      setBusy("");
      setConfirm(null);
    }
  };

  const reinstall = async (serverId) => {
    setBusy(`${serverId}-reinstall`);
    try {
      await axios.post(`${API}/servers/${serverId}/reinstall-request`, {}, { headers: authHeaders() });
      setServers((ss) => ss.map((s) => (s.server_id === serverId ? { ...s, reinstall: { status: "pending" } } : s)));
      toast.success("Reinstall requested — the Wynova team will approve it shortly.");
    } catch {
      toast.error("Could not request reinstall.");
    } finally {
      setBusy("");
      setConfirm(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (servers.length === 0)
    return (
      <div data-testid="servers-empty" className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-16 text-center">
        <Server className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600" />
        <p className="mt-4 font-semibold">No servers yet</p>
        <p className="mt-2 text-sm text-slate-500">Head to the Store tab to deploy your first VPS.</p>
      </div>
    );

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="servers-list">
      {servers.map((s) => (
        <article key={s.server_id} data-testid={`server-card-${s.server_id}`}
                 className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8 hover:border-primary/40 transition-colors">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">{s.name}</h3>
              <p className="text-xs text-slate-500 mt-1">{s.plan_name} · {s.provider === "simulation" ? "Simulation mode" : "Hetzner Cloud"}</p>
            </div>
            <StatusDot status={s.status} />
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3 text-center">
            {[["vCPU", s.specs.vcpu], ["RAM", `${s.specs.ram_gb} GB`], ["NVMe", s.specs.disk_gb >= 1000 ? `${s.specs.disk_gb / 1024} TB` : `${s.specs.disk_gb} GB`]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-slate-50 dark:bg-slate-800/60 py-3">
                <p className="font-display font-bold">{v}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-2 font-mono text-xs">
            <div className="flex items-center justify-between gap-2 rounded-xl bg-slate-950 text-slate-200 px-4 py-2.5">
              <span className="text-slate-500">IP</span>
              <span className="flex items-center gap-2">{s.ip_address || "assigning…"}
                {s.ip_address && <button onClick={() => copy(s.ip_address)} aria-label="Copy IP" className="text-slate-500 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2 rounded-xl bg-slate-950 text-slate-200 px-4 py-2.5">
              <span className="text-slate-500">root</span>
              <span className="flex items-center gap-2">
                {showPw[s.server_id] ? s.root_password : "••••••••••••"}
                <button onClick={() => setShowPw((p) => ({ ...p, [s.server_id]: !p[s.server_id] }))} aria-label="Toggle password" className="text-slate-500 hover:text-white" data-testid={`server-pw-toggle-${s.server_id}`}>
                  {showPw[s.server_id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => copy(s.root_password)} aria-label="Copy password" className="text-slate-500 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
              </span>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={() => power(s.server_id, "start")} disabled={!!busy || s.status === "running"} data-testid={`server-start-${s.server_id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 text-xs font-bold hover:bg-emerald-600/20 disabled:opacity-40 transition-colors">
              {busy === `${s.server_id}-start` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Start
            </button>
            <button onClick={() => setConfirm({ type: "stop", server: s })} disabled={!!busy || s.status === "offline"} data-testid={`server-stop-${s.server_id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-red-600/10 text-red-600 dark:text-red-400 text-xs font-bold hover:bg-red-600/20 disabled:opacity-40 transition-colors">
              <Square className="w-3.5 h-3.5" /> Stop
            </button>
            <button onClick={() => setConfirm({ type: "reboot", server: s })} disabled={!!busy || s.status !== "running"} data-testid={`server-reboot-${s.server_id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-slate-500/10 text-slate-600 dark:text-slate-300 text-xs font-bold hover:bg-slate-500/20 disabled:opacity-40 transition-colors">
              <RotateCw className="w-3.5 h-3.5" /> Reboot
            </button>
            {s.reinstall?.status === "pending" ? (
              <span className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold" data-testid={`server-reinstall-pending-${s.server_id}`}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reinstall pending approval
              </span>
            ) : (
              <button onClick={() => setConfirm({ type: "reinstall", server: s })} disabled={!!busy} data-testid={`server-reinstall-${s.server_id}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold hover:bg-amber-500/20 disabled:opacity-40 transition-colors">
                <RefreshCcw className="w-3.5 h-3.5" /> Reinstall
              </button>
            )}
          </div>
        </article>
      ))}
      {confirm?.type === "stop" && (
        <Confirm title="Stop this server?" testid="confirm-stop"
                 body={`${confirm.server.name} will go offline and any sites on it will stop responding until you start it again.`}
                 confirmLabel="Stop Server" onCancel={() => setConfirm(null)} onConfirm={() => power(confirm.server.server_id, "stop")} />
      )}
      {confirm?.type === "reboot" && (
        <Confirm title="Reboot this server?" testid="confirm-reboot"
                 body={`${confirm.server.name} will restart. Expect a short interruption of about a minute.`}
                 confirmLabel="Reboot" onCancel={() => setConfirm(null)} onConfirm={() => power(confirm.server.server_id, "reboot")} />
      )}
      {confirm?.type === "reinstall" && (
        <Confirm title="Request a reinstall?" testid="confirm-reinstall"
                 body="A reinstall completely wipes this server and installs a fresh system. All data will be lost. The Wynova team reviews and approves every reinstall before it runs."
                 confirmLabel="Request Reinstall" onCancel={() => setConfirm(null)} onConfirm={() => reinstall(confirm.server.server_id)} />
      )}
    </div>
  );
}
